import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    useIonRouter,
    IonItem,
    IonInput,
    IonLabel,
    IonToast,
    IonGrid,
    IonRow,
    IonCol
} from '@ionic/react';
import {
    peopleOutline,
    businessOutline,
    statsChartOutline,
    settingsOutline,
    logOutOutline,
    createOutline,
    saveOutline,
    closeOutline,
    shieldCheckmarkOutline,
    serverOutline,
    helpCircleOutline
} from 'ionicons/icons';
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import './View.css';
import Loader from '../components/Loader';

const SuperAdmin = () => {
    const router = useIonRouter();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalOrgs: 0,
        totalPolls: 0,
        activePolls: 0,
        totalVotes: 0
    });
    const [user, setUser] = useState({
        name: '',
        email: '',
        id: ''
    });

    // Profile Editing State
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: ''
    });
    const [savingProfile, setSavingProfile] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', color: 'success' });

    useEffect(() => {
        const roleId = sessionStorage.getItem('role_id');
        if (!roleId || roleId !== '1') {
            router.push('/page/login');
            return;
        }
        fetchGlobalStats();
    }, []);

    const fetchCurrentUser = async () => {
        const userId = localStorage.getItem('userId');
        try {
            const response = await axios.get(`${API_CONFIG.MANAGE_USERS}?user_id=${userId}&id=${userId}`, { timeout: 8000 });
            if (response.data.success && response.data.users.length > 0) {
                const u = response.data.users[0];
                const fullName = u.name || `${u.first_name} ${u.last_name}`;
                setUser({
                    name: fullName,
                    email: u.email,
                    id: u.id
                });
                setEditForm({ name: fullName, email: u.email });
            }
        } catch (e) {
            console.error("Failed to fetch current user", e);
        }
    };

    const fetchGlobalStats = async () => {
        setLoading(true);
        const safetyTimeout = setTimeout(() => {
            setLoading(false);
            setToast({ show: true, message: 'Server is taking too long to respond. Please check your backend connection.', color: 'warning' });
        }, 12000);

        try {
            const userId = localStorage.getItem('userId');

            // Re-fetch current user info as well
            await fetchCurrentUser();

            // Fetch Real-time System Statistics
            const response = await axios.get(`${API_CONFIG.GET_SUPER_ADMIN_STATS}?user_id=${userId}`, { timeout: 8000 });

            if (response.data.success && response.data.stats) {
                setStats(response.data.stats);
            } else {
                console.error('Failed to fetch stats:', response.data.error);
                setToast({ show: true, message: 'Failed to fetch dashboard statistics.', color: 'warning' });
            }

        } catch (error) {
            console.error('Error fetching stats:', error);
            setToast({ show: true, message: 'Failed to synchronize with server.', color: 'danger' });
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    };

    const handleUpdateProfile = async () => {
        setSavingProfile(true);
        try {
            const userId = localStorage.getItem('userId');
            const nameParts = editForm.name.split(' ');
            const firstName = nameParts[0];
            const lastName = nameParts.slice(1).join(' ') || '';

            const payload = {
                user_id: userId,
                id: userId,
                name: editForm.name,
                first_name: firstName,
                last_name: lastName,
                // Ensure we don't accidentally demote ourselves or deactivate
                role_id: 1,
                is_active: 1
            };

            // Only include email if it was actually changed
            if (editForm.email !== user.email) {
                payload.email = editForm.email;
            }

            const response = await axios.put(API_CONFIG.MANAGE_USERS, payload);

            if (response.data.success) {
                setUser(prev => ({ ...prev, name: editForm.name, email: editForm.email }));
                setIsEditingProfile(false);
                setToast({ show: true, message: 'Profile updated successfully', color: 'success' });
            } else {
                setToast({ show: true, message: response.data.error || 'Update failed', color: 'danger' });
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            const errorMsg = error.response?.data?.error || 'Failed to update profile';
            setToast({ show: true, message: errorMsg, color: 'danger' });
        } finally {
            setSavingProfile(false);
        }
    };

    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = '/page/login';
    };

    const adminActions = [
        {
            title: 'User Management',
            description: 'Manage all system users, suspend accounts, and view roles.',
            icon: peopleOutline,
            color: '#6366f1', // Indigo
            route: '/admin/users'
        },
        {
            title: 'Organization Approvals',
            description: 'Review and approve pending organization registrations.',
            icon: businessOutline,
            color: '#f59e0b', // Amber
            route: '/admin/organizations'
        },
        {
            title: 'System Settings',
            description: 'Configure global system parameters.',
            icon: settingsOutline,
            color: '#14b8a6', // Teal
            route: '/admin/settings'
        }
    ];

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" defaultHref="/page/login" className="custom-back-button" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <span className="title-text">Super Admin </span>
                    </IonTitle>
                    <IonButtons slot="end">
                       {/*  <IonButton>
                            <IonIcon icon={helpCircleOutline} slot="icon-only" />
                        </IonButton> */}
                      {/*   <IonButton color="danger" onClick={handleLogout}>
                            <IonIcon icon={logOutOutline} slot="icon-only" />
                        </IonButton> */}
                        <IonMenuButton autoHide={false} className="lg:hidden relative left-5" />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader />}
                <div className="max-w-6xl mx-auto p-4 space-y-6">

                    {/* Welcome & Profile Section */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Welcome, {user.name}</h1>
                                <p className="text-gray-500">Super Administrator Access</p>
                            </div>
                            <div>
                                <IonButton 
                                    fill="solid" 
                                    className="edit-profile-button"
                                    onClick={() => setIsEditingProfile(!isEditingProfile)}
                                >
                                    <IonIcon icon={isEditingProfile ? closeOutline : createOutline} slot="start" />
                                    {isEditingProfile ? 'Cancel Edit' : 'Edit My Profile'}
                                </IonButton>
                            </div>
                        </div>

                        {/* Inline Profile Editor */}
                        {isEditingProfile && (
                            <div className="mt-6 p-4 bg-gray-50 rounded-xl animate-fade-in border border-blue-100">
                                <h3 className="font-bold text-gray-700 mb-3">Edit Profile Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <IonItem lines="full" className="rounded-lg bg-white">
                                        <IonLabel position="stacked">Full Name</IonLabel>
                                        <IonInput
                                            value={editForm.name}
                                            onIonChange={e => setEditForm(prev => ({ ...prev, name: e.detail.value }))}
                                        />
                                    </IonItem>
                                    <IonItem lines="full" className="rounded-lg bg-white">
                                        <IonLabel position="stacked">Email Access</IonLabel>
                                        <IonInput
                                            value={editForm.email}
                                            onIonChange={e => setEditForm(prev => ({ ...prev, email: e.detail.value }))}
                                        />
                                    </IonItem>
                                </div>
                                <div className="mt-4 flex gap-3">
                                    <IonButton 
                                        className="save-changes-button"
                                        onClick={handleUpdateProfile} 
                                        disabled={savingProfile}
                                    >
                                        <IonIcon icon={saveOutline} slot="start" />
                                        {savingProfile ? 'Saving...' : 'Save Changes'}
                                    </IonButton>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4 md:p-6 shadow-lg">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-blue-100 text-xs md:text-sm font-medium">Total Users</p>
                                    <h2 className="text-xl md:text-3xl font-bold mt-1">{stats.totalUsers}</h2>
                                </div>
                                <div className="bg-white/20 p-2 md:p-3 rounded-lg">
                                    <IonIcon icon={peopleOutline} className="text-xl md:text-2xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-4 md:p-6 shadow-lg">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-emerald-100 text-xs md:text-sm font-medium">Total Polls</p>
                                    <h2 className="text-xl md:text-3xl font-bold mt-1">{stats.totalPolls}</h2>
                                </div>
                                <div className="bg-white/20 p-2 md:p-3 rounded-lg">
                                    <IonIcon icon={statsChartOutline} className="text-xl md:text-2xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl p-4 md:p-6 shadow-lg">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-indigo-100 text-xs md:text-sm font-medium">Total Votes</p>
                                    <h2 className="text-xl md:text-3xl font-bold mt-1">{stats.totalVotes}</h2>
                                </div>
                                <div className="bg-white/20 p-2 md:p-3 rounded-lg">
                                    <IonIcon icon={shieldCheckmarkOutline} className="text-xl md:text-2xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-4 md:p-6 shadow-lg">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-orange-100 text-xs md:text-sm font-medium">Active Polls</p>
                                    <h2 className="text-xl md:text-3xl font-bold mt-1">{stats.activePolls}</h2>
                                </div>
                                <div className="bg-white/20 p-2 md:p-3 rounded-lg">
                                    <IonIcon icon={serverOutline} className="text-xl md:text-2xl" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Cards */}
                    <h2 className="text-xl font-bold text-gray-800 border-l-4 border-blue-600 pl-3">Administration & Management</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {adminActions.map((action, idx) => (
                            <IonCard
                                key={idx}
                                className="m-0 shadow-md hover:shadow-xl transition-shadow cursor-pointer border-t-4"
                                style={{ borderTopColor: action.color }}
                                onClick={() => action.route !== '#' && router.push(action.route)}
                            >
                                <IonCardContent className="p-6">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div 
                                            className="p-3 rounded-full"
                                            style={{ backgroundColor: `${action.color}15`, color: action.color }}
                                        >
                                            <IonIcon icon={action.icon} className="text-2xl" />
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-lg">{action.title}</h3>
                                    </div>
                                    <p className="text-gray-600 text-sm">{action.description}</p>
                                </IonCardContent>
                            </IonCard>
                        ))}
                    </div>

                </div>
            </IonContent>
            <IonToast
                isOpen={toast.show && !!toast.message}
                onDidDismiss={() => setToast({ ...toast, show: false })}
                message={toast.message}
                duration={3000}
                color={toast.color}
                position="top"
            />
        </IonPage>

    );
};

export default SuperAdmin;
